/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { Link } from 'react-router-dom';
import { Text, Modal } from '@blend/enterprise-components';
import { inputFormatters } from '@blend/shared-react';

import * as cookieUtils from 'id-utils/cookies';
import { ModalContent } from 'id-components/ModalContent';
import { WORKSPACE_SUPPORT_EMAIL, WORKSPACE_SUPPORT_PHONE } from 'id-constants/global';
import { getConfig } from 'id-utils/config';
import { isWorkspaceUser } from 'id-utils/login';

import { css, StyleSheet } from 'aphrodite';

export const styles = StyleSheet.create({
  textBlock: {
    marginBottom: 8,
  },
});

export namespace SigninHelpModal {
  export interface Props {
    handleClose: () => void;
    isOpen: boolean;
  }
}
export const SigninHelpModal: React.FC<SigninHelpModal.Props> = ({ isOpen, handleClose }) => {
  const config = getConfig();
  const formattedWorkspaceSupportPhone = inputFormatters.PHONE.format(WORKSPACE_SUPPORT_PHONE);
  const clientAppIdFromCookie = cookieUtils.getCookieValue(config.clientAppIdCookieName) || '';

  const defaultSupportContact = (
    <p>
      <Text id="it-DefaultContact">
        If you still can't log in after resetting your password, please{' '}
        <a href="https://help.blend.com/hc/en-us/requests/new" target="_blank">
          contact support
        </a>
        .
      </Text>
    </p>
  );

  const workspaceSupportContact = (
    <p>
      <Text id="it-WorkspaceContact">
        If you still can't log in after resetting your password, please contact{' '}
        <a href={`mailto:${WORKSPACE_SUPPORT_EMAIL}`}>{WORKSPACE_SUPPORT_EMAIL}</a> or call{' '}
        <a href={`tel:${WORKSPACE_SUPPORT_PHONE}`}>{formattedWorkspaceSupportPhone}</a>.
      </Text>
    </p>
  );
  return (
    <Modal contentLabel="Signin help" handleClose={handleClose} isOpen={isOpen}>
      <ModalContent handleClose={handleClose} headerText="Trouble signing in?">
        <p className={css(styles.textBlock)}>
          <Text>
            If you are having trouble logging in, please use the{' '}
            <Link to="/client/forgot-password">Forgot Password</Link> functionality on the login page to send yourself
            an email with instructions on resetting your password or activating your account (if this is your first time
            signing in).
          </Text>
        </p>
        {isWorkspaceUser(clientAppIdFromCookie) ? workspaceSupportContact : defaultSupportContact}
      </ModalContent>
    </Modal>
  );
};

SigninHelpModal.displayName = 'SigninHelpModal';
